import { useEffect, useState } from "react";
import { api } from "../api";

interface Props {
  open: boolean;
  runId: string;
  /** Aktueller ratio aus 01-classification (zur Vorab-Anzeige). */
  currentRatio: number;
  /** Wenn der User vorher 2 Punkte im Plan gesetzt hat (via editMode="scalePicking"). */
  prefilledTwoPoint?: {
    from: { x: number; y: number };
    to: { x: number; y: number };
  } | null;
  onClose: () => void;
  /** Wechselt in den 2-Punkt-Picking-Modus auf der Plan-SVG. Modal schließt. */
  onRequestTwoPointPicking: () => void;
  /** Nach erfolgreicher Server-Antwort — Caller lädt das Bundle neu. */
  onSuccess: (newRatio: number) => void;
}

const PT_TO_MM_PAPER = 25.4 / 72;

export default function ScaleSetter({
  open,
  runId,
  currentRatio,
  prefilledTwoPoint,
  onClose,
  onRequestTwoPointPicking,
  onSuccess,
}: Props) {
  const [mode, setMode] = useState<"ratio" | "twoPoint">("ratio");
  const [ratioInput, setRatioInput] = useState<string>("");
  const [twoPointMm, setTwoPointMm] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Wenn Modal geöffnet wird, Felder vorbefüllen.
  useEffect(() => {
    if (open) {
      setError(null);
      setSubmitting(false);
      if (prefilledTwoPoint) {
        setMode("twoPoint");
      }
      setRatioInput(String(Math.round(currentRatio)));
      setTwoPointMm("");
    }
  }, [open, currentRatio, prefilledTwoPoint]);

  if (!open) return null;

  const previewRatio = (() => {
    if (mode === "ratio") {
      const n = Number(ratioInput.replace(",", "."));
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    if (mode === "twoPoint" && prefilledTwoPoint) {
      const dx = prefilledTwoPoint.to.x - prefilledTwoPoint.from.x;
      const dy = prefilledTwoPoint.to.y - prefilledTwoPoint.from.y;
      const lenPt = Math.hypot(dx, dy);
      const realMm = Number(twoPointMm.replace(",", "."));
      if (
        Number.isFinite(realMm) &&
        realMm > 0 &&
        Number.isFinite(lenPt) &&
        lenPt > 1
      ) {
        return realMm / (lenPt * PT_TO_MM_PAPER);
      }
    }
    return null;
  })();

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (mode === "ratio") {
        const n = Number(ratioInput.replace(",", "."));
        if (!Number.isFinite(n) || n <= 0) {
          throw new Error("Bitte eine positive Zahl eingeben (z.B. 100).");
        }
        const r = await api.multiagentSetScale(runId, {
          mode: "ratio",
          ratio: n,
        });
        onSuccess(r.newRatio);
      } else {
        if (!prefilledTwoPoint) {
          throw new Error(
            "Erst auf 'Zwei Punkte im Plan setzen' klicken, dann Real-Maß eingeben."
          );
        }
        const realMm = Number(twoPointMm.replace(",", "."));
        if (!Number.isFinite(realMm) || realMm <= 0) {
          throw new Error("Bitte ein positives Real-Maß in Millimetern eingeben.");
        }
        const r = await api.multiagentSetScale(runId, {
          mode: "twoPoint",
          from: prefilledTwoPoint.from,
          to: prefilledTwoPoint.to,
          realDistanceMm: realMm,
        });
        onSuccess(r.newRatio);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-30 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="scale-setter-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h2 id="scale-setter-title" className="text-lg font-semibold">
            Maßstab manuell setzen
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Aktuell: 1:{currentRatio.toFixed(0)} · neue Eingabe wird mit
            <code className="bg-slate-100 px-1 rounded mx-1">source: user</code>
            gepinned (we-stamps-Pfad blockiert).
          </p>
        </div>

        <fieldset className="space-y-2">
          <legend className="sr-only">Modus wählen</legend>
          <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-slate-200 hover:bg-slate-50">
            <input
              type="radio"
              name="scale-mode"
              value="ratio"
              checked={mode === "ratio"}
              onChange={() => setMode("ratio")}
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="font-medium">Verhältnis (z.B. 1:100)</div>
              <div className="text-xs text-slate-500">
                Direkter Maßstabs-Nenner — wenn der Plan-Header ihn ausweist.
              </div>
            </div>
          </label>
          <label className="flex items-start gap-2 cursor-pointer p-2 rounded border border-slate-200 hover:bg-slate-50">
            <input
              type="radio"
              name="scale-mode"
              value="twoPoint"
              checked={mode === "twoPoint"}
              onChange={() => setMode("twoPoint")}
              className="mt-0.5"
            />
            <div className="text-sm">
              <div className="font-medium">Zwei-Punkt-Methode</div>
              <div className="text-xs text-slate-500">
                Zwei Plan-Punkte mit bekanntem Real-Maß setzen — exakter bei
                Raster-Plänen ohne Maßstabs-Stempel.
              </div>
            </div>
          </label>
        </fieldset>

        {mode === "ratio" && (
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">
              Verhältnis 1:
            </label>
            <input
              type="number"
              inputMode="numeric"
              value={ratioInput}
              onChange={(e) => setRatioInput(e.target.value)}
              placeholder="100"
              className="w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              min={1}
              max={5000}
            />
            <p className="text-xs text-slate-500 mt-1">
              z.B. 100 für 1:100, 50 für 1:50.
            </p>
          </div>
        )}

        {mode === "twoPoint" && (
          <div className="space-y-3">
            {!prefilledTwoPoint ? (
              <div className="p-3 bg-slate-50 border rounded text-sm">
                <p className="text-slate-700 mb-2">
                  Zwei Punkte mit bekanntem Real-Maß im Plan setzen.
                </p>
                <button
                  onClick={onRequestTwoPointPicking}
                  className="w-full px-3 py-2 text-sm rounded bg-brand-600 text-white hover:bg-brand-700"
                >
                  Zwei Punkte im Plan setzen →
                </button>
              </div>
            ) : (
              <>
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs">
                  <div className="font-medium text-emerald-900 mb-1">
                    Punkte gesetzt:
                  </div>
                  <div className="font-mono text-emerald-800">
                    A: ({prefilledTwoPoint.from.x.toFixed(1)},{" "}
                    {prefilledTwoPoint.from.y.toFixed(1)})
                    <br />
                    B: ({prefilledTwoPoint.to.x.toFixed(1)},{" "}
                    {prefilledTwoPoint.to.y.toFixed(1)})
                    <br />
                    Distanz im Plan:{" "}
                    {Math.hypot(
                      prefilledTwoPoint.to.x - prefilledTwoPoint.from.x,
                      prefilledTwoPoint.to.y - prefilledTwoPoint.from.y
                    ).toFixed(2)}{" "}
                    pt
                  </div>
                  <button
                    onClick={onRequestTwoPointPicking}
                    className="mt-1 text-xs text-emerald-700 underline hover:text-emerald-900"
                  >
                    Andere Punkte wählen
                  </button>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Real-Maß zwischen A und B (in mm)
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={twoPointMm}
                    onChange={(e) => setTwoPointMm(e.target.value)}
                    placeholder="z.B. 5000 für 5 Meter"
                    className="w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  />
                </div>
              </>
            )}
          </div>
        )}

        {previewRatio !== null && (
          <div className="text-xs bg-slate-50 border rounded p-2 text-slate-700">
            <span className="font-medium">Vorschau:</span> 1 mm im Plan ={" "}
            <span className="font-mono">{previewRatio.toFixed(2)}</span> mm real
            (1:{previewRatio.toFixed(0)})
          </div>
        )}

        {error && (
          <div role="alert" className="text-sm bg-red-50 border border-red-200 text-red-800 rounded p-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            onClick={submit}
            disabled={submitting || previewRatio === null}
            className="px-4 py-1.5 text-sm rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-wait"
          >
            {submitting ? "Setze…" : "Setzen"}
          </button>
        </div>
      </div>
    </div>
  );
}
